import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit } from '@angular/core';
import { filter, Observable, take } from 'rxjs';
import { GuidedTour, GuidedTourService, GuidedTourState } from '@markant-platform/shared/guided-tour';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { ArrayIncludesPipe, PercentagePipe } from '@markant-platform/shared/util-pipes';
import { DashboardService } from '../../../../services/dashboard.service';
import { WidgetIdentity } from '../../../../models/dashboard.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'markant-platform-guided-tour-widget',
  templateUrl: './guided-tour-widget.component.html',
  styleUrls: ['./guided-tour-widget.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MarkantElementsModule, TranslocoModule, PercentagePipe, ArrayIncludesPipe],
})
export class GuidedTourWidgetComponent implements OnInit {
  @Input()
  set guidedTour(guidedTour: GuidedTour) {
    this._guidedTourInput = guidedTour;
  }

  private _guidedTourInput!: GuidedTour;

  guidedTour$!: Observable<GuidedTourState>;

  constructor(
    private readonly tourService: GuidedTourService,
    private readonly dashboardService: DashboardService,
    private readonly destroyRef: DestroyRef
  ) {}

  async ngOnInit(): Promise<void> {
    this.guidedTour$ = this.tourService.guidedTour$(this._guidedTourInput.id);
    await this.tourService.initGuidedTour(this._guidedTourInput);

    this.listenToWidgetRemoval();
  }

  private listenToWidgetRemoval(): void {
    this.tourService
      .isWidgetRemoved$(this._guidedTourInput.id)
      .pipe(
        filter(isRemoved => isRemoved),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const widget: WidgetIdentity = { id: this._guidedTourInput.id };
        this.dashboardService.triggerWidgetRemoval(widget);
        this.tourService.widgetRemoved(this._guidedTourInput.id);
      });
  }

  public async startGuidedTour(section: number): Promise<void> {
    await this.tourService.startSection(this._guidedTourInput.id, section);
  }
}
